import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Heart, X, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Favorites = ({ progress, updateProgress }) => {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState(progress?.favorites || []);

  const allSubjects = [
    // 7+
    { id: 'maths-7', name: 'Maths', category: '7+', color: 'bg-blue-100 border-blue-300' },
    { id: 'english-7', name: 'English', category: '7+', color: 'bg-green-100 border-green-300' },
    { id: 'reasoning-7', name: 'Reasoning', category: '7+', color: 'bg-purple-100 border-purple-300' },
    // 11+
    { id: 'maths-11', name: 'Maths', category: '11+', color: 'bg-blue-100 border-blue-300' },
    { id: 'english-11', name: 'English', category: '11+', color: 'bg-green-100 border-green-300' },
    { id: 'verbal-reasoning-11', name: 'Verbal Reasoning', category: '11+', color: 'bg-purple-100 border-purple-300' },
    { id: 'non-verbal-reasoning-11', name: 'Non-Verbal Reasoning', category: '11+', color: 'bg-orange-100 border-orange-300' },
    // GCSE
    { id: 'maths-gcse', name: 'Mathematics', category: 'GCSE', color: 'bg-blue-100 border-blue-300' },
    { id: 'english-lang-gcse', name: 'English Language', category: 'GCSE', color: 'bg-green-100 border-green-300' },
    { id: 'english-lit-gcse', name: 'English Literature', category: 'GCSE', color: 'bg-teal-100 border-teal-300' },
    { id: 'sciences-gcse', name: 'Combined Science', category: 'GCSE', color: 'bg-purple-100 border-purple-300' },
    { id: 'history-gcse', name: 'History', category: 'GCSE', color: 'bg-amber-100 border-amber-300' },
    { id: 'geography-gcse', name: 'Geography', category: 'GCSE', color: 'bg-cyan-100 border-cyan-300' },
    // A-Levels
    { id: 'maths-a', name: 'Mathematics', category: 'A-Levels', color: 'bg-blue-100 border-blue-300' },
    { id: 'further-maths-a', name: 'Further Maths', category: 'A-Levels', color: 'bg-indigo-100 border-indigo-300' },
    { id: 'physics-a', name: 'Physics', category: 'A-Levels', color: 'bg-purple-100 border-purple-300' },
    { id: 'chemistry-a', name: 'Chemistry', category: 'A-Levels', color: 'bg-green-100 border-green-300' },
    { id: 'biology-a', name: 'Biology', category: 'A-Levels', color: 'bg-emerald-100 border-emerald-300' },
    { id: 'english-lit-a', name: 'English Literature', category: 'A-Levels', color: 'bg-rose-100 border-rose-300' }
  ];

  const toggleFavorite = async (subjectId) => {
    const isFavorite = favorites.includes(subjectId);
    const newFavorites = isFavorite
      ? favorites.filter(id => id !== subjectId)
      : [...favorites, subjectId];
    
    setFavorites(newFavorites);
    
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await axios.post(
          `${API}/favorites/update`,
          { favorites: newFavorites },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      updateProgress({ favorites: newFavorites });
      toast.success(isFavorite ? 'Removed from favorites' : 'Added to favorites');
    } catch (error) {
      console.error('Favorites update error:', error);
      setFavorites(favorites); // Revert on error
      toast.error('Failed to update favorites');
    }
  };

  const groupedSubjects = allSubjects.reduce((acc, subject) => {
    if (!acc[subject.category]) {
      acc[subject.category] = [];
    }
    acc[subject.category].push(subject);
    return acc;
  }, {});

  const favoriteSubjects = allSubjects.filter(s => favorites.includes(s.id));

  return (
    <div className="min-h-screen p-4 md:p-8" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <div className="max-w-7xl mx-auto space-y-8 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Space Grotesk' }}>
              My Favorites
            </h1>
            <p className="text-xl text-white/90">
              Select subjects to add to your favorites for quick access
            </p>
          </div>
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="text-white hover:bg-white/20"
            data-testid="back-btn"
          >
            Done
          </Button>
        </div>

        {/* Current Favorites */}
        {favoriteSubjects.length > 0 && (
          <Card className="shadow-xl" data-testid="favorites-list">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="h-6 w-6 text-red-500 fill-red-500" />
                Your Favorite Subjects ({favoriteSubjects.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {favoriteSubjects.map((subject) => (
                  <Badge
                    key={subject.id}
                    className="px-4 py-2 text-base bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 cursor-pointer"
                    onClick={() => toggleFavorite(subject.id)}
                  >
                    {subject.category} - {subject.name}
                    <X className="ml-2 h-4 w-4" />
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* All Subjects by Category */}
        {Object.entries(groupedSubjects).map(([category, subjects]) => (
          <Card key={category} className="shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl">{category}</CardTitle>
              <CardDescription>Select subjects you want to focus on</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-4 gap-4">
                {subjects.map((subject) => {
                  const isFavorite = favorites.includes(subject.id);
                  return (
                    <Card
                      key={subject.id}
                      className={`cursor-pointer transition-all hover:shadow-lg ${
                        isFavorite ? 'ring-2 ring-pink-500' : ''
                      }`}
                      onClick={() => toggleFavorite(subject.id)}
                      data-testid={`subject-card-${subject.id}`}
                    >
                      <CardContent className={`p-4 ${subject.color} border-2 relative`}>
                        <div className="absolute top-2 right-2">
                          <Heart
                            className={`h-5 w-5 ${
                              isFavorite ? 'text-red-500 fill-red-500' : 'text-gray-400'
                            }`}
                          />
                        </div>
                        <p className="font-bold text-gray-800 mt-2">{subject.name}</p>
                        <p className="text-xs text-gray-600">{category}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}

        {favoriteSubjects.length === 0 && (
          <Card className="shadow-xl">
            <CardContent className="p-12 text-center">
              <BookOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No favorites yet</h3>
              <p className="text-gray-500">Click on subjects above to add them to your favorites</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Favorites;
